import React from 'react';
import { Line } from 'react-chartjs-2';
import { useExecutiveTrendAnalysis } from '../../hooks/useExecutiveQueries';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartColors, getChartBackgroundColor, getChartJsOptions } from '../../utils/themeHelpers';
import { LoadingSpinner, ChartInfoButton } from '../ui';
import { CHART_GUIDES } from '../../constants/chartGuides';
import useChartGuide from '../../hooks/useChartGuide';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface TrendAnalysisChartProps {
  branchId?: number;
  timeRange?: any;
  onError?: (error: Error) => void;
  showLoading?: boolean;
  compact?: boolean;
}

const TrendAnalysisChart: React.FC<TrendAnalysisChartProps> = ({
  branchId,
  timeRange,
  onError,
  showLoading = true,
  compact = false
}) => {
  const { currentTheme } = useTheme();
  const chartColors = getChartColors(currentTheme);
  const { openGuide } = useChartGuide();
  
  // Fetch trend analysis data with timeRange
  const { data, isLoading, error } = useExecutiveTrendAnalysis(branchId, timeRange?.timeRange);
  
  const trendData = data || {
    labels: [],
    creditUsageRate: [],
    creditGenerationRate: [],
    creditToRevenue: [],
    customerEngagementRate: []
  };
  
  // Chart options
  const percentageChartOptions = {
    ...getChartJsOptions(currentTheme),
    scales: {
      ...getChartJsOptions(currentTheme).scales,
      x: {
        ...getChartJsOptions(currentTheme).scales.x,
        ticks: {
          ...getChartJsOptions(currentTheme).scales?.x?.ticks,
          autoSkip: false,
          maxRotation: 45,
          minRotation: 45,
        },
      },
      y: {
        ...getChartJsOptions(currentTheme).scales.y,
      }
    }
  };
  
  // Handle loading state
  if (showLoading && isLoading) {
    return (
      <div className="card-base card-padding">
        <div className="flex items-center justify-center min-h-[350px]">
          <LoadingSpinner
            variant="minimal"
            size="lg"
            message="در حال بارگذاری نمودار روند…"
          />
        </div>
      </div>
    );
  }
  
  // Handle error
  if (error) {
    if (onError && error instanceof Error) {
      onError(error);
    }
    return null;
  }
  
  return (
    <div className="card-base card-padding animate-enter animate-delay-3">
      <ChartInfoButton 
        onClick={() => openGuide(CHART_GUIDES.keyIndicatorsTrend)}
        className="mb-4"
      />
      <div className="mb-6">
        <h3 className="title-card">تحلیل روند شاخص‌های کلیدی در طول دوره</h3>
        <p className="text-text-secondary text-sm mt-1 text-center">مقایسه نرخ تولید و مصرف اعتبار در بازه زمانی انتخاب شده</p>
      </div>
      
      <div style={{ height: compact ? 300 : 350, width: '100%' }}>
        {trendData.labels.length > 0 ? (
          <Line
            data={{
              labels: trendData.labels,
              datasets: [
                {
                  label: "نرخ مصرف اعتبار (%)",
                  data: trendData.creditUsageRate,
                  borderColor: getChartColors(currentTheme)[0],
                  backgroundColor: getChartBackgroundColor(0, currentTheme, 0.1),
                  borderWidth: 3,
                  pointRadius: 5,
                  pointHoverRadius: 7,
                  pointBackgroundColor: getChartColors(currentTheme)[0],
                  tension: 0.3,
                  fill: false,
                },
                {
                  label: "نرخ تولید اعتبار (%)",
                  data: trendData.creditGenerationRate,
                  borderColor: getChartColors(currentTheme)[1],
                  backgroundColor: getChartBackgroundColor(1, currentTheme, 0.1),
                  borderWidth: 3,
                  pointRadius: 5,
                  pointHoverRadius: 7,
                  pointBackgroundColor: getChartColors(currentTheme)[1],
                  tension: 0.3,
                  fill: false,
                }
              ]
            }}
            options={percentageChartOptions}
            aria-label="نمودار روند مقایسه نرخ تولید و مصرف اعتبار"
          />
        ) : (
          <div className="flex items-center justify-center p-8 text-text-tertiary text-sm">
            <p>داده‌ای برای نمایش نمودار وجود ندارد</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrendAnalysisChart;